import { Badge, Banner, Button, Checkbox, Dialog, Field, Input, InputArea, Switch } from '@cloudflare/kumo';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { AiToolsPicker } from '../../components/flow-nodes/AiToolsPicker';
import './AiSkillsPage.css';

interface Example { cliente: string; agente: string }
interface Skill {
  id: string; name: string; slug: string; description: string; instructions: string; tools: string[]; examples: Example[]; active: boolean;
  keywords: string[]; scope: string[]; relatedTools: string[]; risks: string | null; version: number; status: 'published' | 'draft';
  needsReview: boolean; reviewReason: string | null; updatedAt: string;
}
interface Version { id: string; version: number; name: string; description: string; instructions: string; risks: string | null; createdAt: string }
type Draft = Pick<Skill, 'name' | 'description' | 'instructions' | 'tools' | 'examples' | 'keywords' | 'scope' | 'relatedTools' | 'status'> & {
  id?: string; slug?: string; active?: boolean; risks: string; needsReview?: boolean; reviewReason?: string | null; version?: number;
};

const SCOPE_LABEL: Record<string, string> = {
  campanhas: 'Campanhas', contatos: 'Contatos', segmentos: 'Segmentos', sequencias: 'Sequências', conexoes: 'Conexões',
  atendimento: 'Atendimento', cobranca: 'Cobrança', lgpd: 'LGPD', ia: 'IA',
};

const emptyDraft = (): Draft => ({
  name: '', description: '', instructions: '# Regras\n\n- ', tools: [], examples: [], keywords: [], scope: [], relatedTools: [], risks: '', status: 'published',
});
const csv = (v: string) => v.split(',').map((x) => x.trim()).filter(Boolean);

/**
 * IA › Skills: administração do CONHECIMENTO da organização (regras, critérios,
 * procedimentos e riscos). A Lumi e os agentes consultam as Skills relevantes a
 * cada pedido; elas não executam nada e não liberam ferramentas.
 */
export function AiSkillsPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const navigate = useNavigate();
  const toast = useAppToast();
  const importRef = useRef<HTMLInputElement>(null);
  const [skills, setSkills] = useState<Skill[] | null>(null);
  const [mode, setMode] = useState<'knowledge' | 'legacy'>('legacy');
  const [scopes, setScopes] = useState<string[]>(Object.keys(SCOPE_LABEL));
  const [locked, setLocked] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [generating, setGenerating] = useState(false);
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState<Skill | null>(null);
  const [history, setHistory] = useState<{ skill: Skill; versions: Version[]; open: Version | null } | null>(null);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/ai/skills');
    const j = await r.json().catch(() => ({}));
    if (r.status === 403) { setLocked(j.message || 'Recurso não incluído no seu plano'); return; }
    if (r.ok) { setSkills(j.skills); if (j.mode) setMode(j.mode); if (j.scopes) setScopes(j.scopes); }
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const call = async (url: string, init: RequestInit) => {
    setBusy(true);
    const r = await fetchApi(url, init);
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível concluir'); return null; }
    return j;
  };

  const save = async () => {
    if (!draft) return;
    const body = JSON.stringify({
      name: draft.name, description: draft.description, instructions: draft.instructions, tools: draft.tools, examples: draft.examples,
      keywords: draft.keywords, scope: draft.scope, relatedTools: draft.relatedTools, risks: draft.risks, status: draft.status,
    });
    const j = await call(draft.id ? `/api/ai/skills/${draft.id}` : '/api/ai/skills', { method: draft.id ? 'PUT' : 'POST', body });
    if (j) {
      toast.success(j.skill.needsReview ? 'Skill salva — ainda parece uma tarefa; revise o texto' : `Skill salva (versão ${j.skill.version})`);
      setDraft(null); load();
    }
  };

  const markReviewed = async (s: Skill) => {
    if (await call(`/api/ai/skills/${s.id}`, { method: 'PUT', body: JSON.stringify({ reviewed: true }) })) { toast.success('Marcada como revisada'); load(); }
  };

  const openHistory = async (s: Skill) => {
    const r = await fetchApi(`/api/ai/skills/${s.id}/versions`);
    if (!r.ok) { toast.error('Não foi possível abrir o histórico'); return; }
    setHistory({ skill: s, versions: (await r.json()).versions, open: null });
  };

  const restore = async (v: Version) => {
    if (!history) return;
    const j = await call(`/api/ai/skills/${history.skill.id}/versions/${v.version}/restore`, { method: 'POST' });
    if (j) { toast.success(`Conteúdo da versão ${v.version} restaurado (nova versão ${j.skill.version})`); setHistory(null); load(); }
  };

  const importMd = async (file: File) => {
    const markdown = await file.text();
    const j = await call('/api/ai/skills', { method: 'POST', body: JSON.stringify({ markdown }) });
    if (j) { toast.success(`Skill "${j.skill.name}" importada`); load(); }
  };

  const exportMd = async (s: Skill) => {
    const r = await fetchApi(`/api/ai/skills/${s.id}/export`);
    if (!r.ok) { toast.error('Não foi possível exportar'); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `${s.slug}.SKILL.md`; a.click();
    URL.revokeObjectURL(url);
  };

  const generate = async () => {
    const j = await call('/api/ai/skills/generate', { method: 'POST', body: JSON.stringify({ goal }) });
    if (j) {
      setGenerating(false); setGoal('');
      setDraft({ ...emptyDraft(), ...j.draft, relatedTools: j.draft.relatedTools ?? j.draft.tools ?? [], examples: [], risks: j.draft.risks ?? '' });
      toast.success('Rascunho pronto: revise e salve');
    }
  };

  const edit = (s: Skill) => setDraft({ ...s, risks: s.risks ?? '' });
  const pending = skills?.filter((s) => s.needsReview) ?? [];

  if (locked) {
    return <div className="page-content" style={{ paddingTop: 28 }}><Banner variant="default" description={<>{locked}</>} /></div>;
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div style={{ maxWidth: 780 }}>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Skills</h2>
            <div className="text-kumo-subtle">
              O conhecimento da sua empresa: regras, critérios, procedimentos e riscos. A Lumi e os agentes de atendimento consultam as Skills
              relevantes para cada pedido. Skills <strong>não executam ações</strong> e não liberam ferramentas: o que pode ser feito depende das
              permissões de cada pessoa e da configuração do agente. Cada salvamento gera uma versão. Formato compatível com <code>SKILL.md</code>.
            </div>
          </div>
          {canEdit && !draft && (
            <div className="flex gap-2 flex-wrap">
              <Button variant="secondary" icon={ICONS.Upload} onClick={() => importRef.current?.click()}>Importar SKILL.md</Button>
              <input ref={importRef} type="file" accept=".md,text/markdown,text/plain" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) importMd(f); e.target.value = ''; }} />
              <Button variant="secondary" icon={ICONS.Sparkle} onClick={() => setGenerating(true)}>Gerar com IA</Button>
              <Button variant="secondary" icon={ICONS.Apps} onClick={() => navigate('/ia/skills/galeria')}>Galeria</Button>
              <Button variant="primary" icon={ICONS.Plus} onClick={() => setDraft(emptyDraft())}>Nova skill</Button>
            </div>
          )}
        </div>
      </div>

      <div className="page-content space-y-6">
        {!draft && pending.length > 0 && (
          <Banner variant="alert" description={<>
            {pending.length === 1 ? '1 Skill está escrita como tarefa' : `${pending.length} Skills estão escritas como tarefa`} ("faça X, chame Y").
            Reescreva como regras e critérios: quem decide o que executar é a Lumi, com as permissões de cada pessoa.
          </>} />
        )}
        {!draft && skills && skills.length === 0 && <Banner variant="default" description={<>Nenhuma skill ainda. Comece pela galeria (critérios de leads, cobrança, LGPD…), gere uma com IA ou importe um SKILL.md.</>} />}

        {!draft && skills && skills.length > 0 && (
          <div className="skills-grid">
            {skills.map((s) => (
              <section key={s.id} className={`brand-card space-y-2 ${s.needsReview ? 'skill-card--review' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{s.name}</div>
                  {canEdit
                    ? <Switch onCheckedChange={async (checked: boolean) => { const v = checked; if (await call(`/api/ai/skills/${s.id}`, { method: 'PUT', body: JSON.stringify({ active: v }) })) load(); }} aria-label="Ativa" checked={s.active} />
                    : <Badge variant={s.active ? 'green' : 'neutral'}>{s.active ? 'Ativa' : 'Pausada'}</Badge>}
                </div>
                <div className="skill-meta">
                  <Badge variant="neutral">v{s.version}</Badge>
                  {s.status === 'draft' && <Badge variant="orange">Rascunho</Badge>}
                  {s.needsReview && <Badge variant="red">Revisar</Badge>}
                  {s.scope.map((x) => <Badge variant="blue" key={x}>{SCOPE_LABEL[x] ?? x}</Badge>)}
                </div>
                <div className="text-sm !text-[var(--text-muted)]">{s.description}</div>
                {s.needsReview && s.reviewReason && <div className="text-xs skill-review-reason">{s.reviewReason}</div>}
                {s.relatedTools.length > 0 && (
                  <div className="skill-related" title="Tools relacionadas: só indicação, não liberam nada">
                    <ICONS.Link size={16} />{s.relatedTools.join(', ')}
                  </div>
                )}
                <div className="flex gap-2 pt-1 flex-wrap">
                  {canEdit && <Button variant="secondary" icon={ICONS.Edit} onClick={() => edit(s)}>Editar</Button>}
                  {canEdit && s.needsReview && <Button variant="secondary" icon={ICONS.Checkmark} onClick={() => markReviewed(s)}>Revisada</Button>}
                  <Button variant="secondary" icon={ICONS.Clock} onClick={() => openHistory(s)}>Versões</Button>
                  <Button variant="secondary" icon={ICONS.Download} onClick={() => exportMd(s)}>SKILL.md</Button>
                  {canEdit && <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={() => setDeleting(s)}>Excluir</DangerButton>}
                </div>
              </section>
            ))}
          </div>
        )}

        {draft && (
          <div className="skills-editor">
            <section className="brand-card space-y-4">
              {draft.needsReview && <Banner variant="alert" description={<>{draft.reviewReason || 'Esta Skill parece uma tarefa. Reescreva como regras e critérios.'}</>} />}
              <Field label="Nome">
                <Input onChange={(e) => setDraft({ ...draft, name: e.target.value })} value={draft.name} placeholder="Critérios de qualificação de leads" />
              </Field>
              <InputArea label="Quando vale" description="Em que situações este conhecimento se aplica (é o que a Lumi lê para escolher a Skill)" onChange={(e) => setDraft({ ...draft, description: e.target.value })} rows={2} value={draft.description} />
              <InputArea label="Conhecimento (markdown)" description="Regras, critérios e procedimentos. Evite mandar chamar ferramentas: diga o que vale, não o que clicar." onChange={(e) => setDraft({ ...draft, instructions: e.target.value })} rows={16} value={draft.instructions} className="skill-mono" />
              <InputArea label="Riscos e limites" description="O que nunca fazer, cuidados legais, limites de desconto…" onChange={(e) => setDraft({ ...draft, risks: e.target.value })} rows={3} value={draft.risks} />
              <div className="flex justify-between items-center gap-2 flex-wrap">
                <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, status: checked ? 'draft' : 'published' })} checked={draft.status === 'draft'} label={<>
                  Rascunho (a Lumi e os agentes ainda não usam)
                </>} />
                <div className="flex gap-2">
                  <Button variant="secondary" onClick={() => setDraft(null)}>Cancelar</Button>
                  <Button variant="primary" loading={busy} disabled={!draft.name.trim() || !draft.description.trim() || !draft.instructions.trim()} onClick={save}>Salvar</Button>
                </div>
              </div>
            </section>
            <section className="brand-card space-y-4">
              <Field label="Palavras-chave" description="Separadas por vírgula; ajudam a escolher esta Skill">
                <Input onChange={(e) => setDraft({ ...draft, keywords: csv(e.target.value) })} value={draft.keywords.join(', ')} placeholder="boleto, atraso, segunda via" />
              </Field>
              <div>
                <div className="font-semibold">Onde vale</div>
                <div className="text-xs !text-[var(--text-muted)]">Telas em que esta Skill tem prioridade</div>
                <div className="skill-scopes">
                  {scopes.map((x) => (
                    <Checkbox onCheckedChange={(checked: boolean) => setDraft({ ...draft, scope: checked ? [...draft.scope, x] : draft.scope.filter((y) => y !== x) })} key={x} checked={draft.scope.includes(x)} label={<>
                      {SCOPE_LABEL[x] ?? x}
                    </>} />
                  ))}
                </div>
              </div>
              <Field label="Tools relacionadas" description="Só indicação (não liberam nada). Ex.: update_contact, gerar_cupom">
                <Input onChange={(e) => setDraft({ ...draft, relatedTools: csv(e.target.value) })} value={draft.relatedTools.join(', ')} />
              </Field>
              {mode === 'legacy' && (
                <details className="skill-legacy">
                  <summary>Modo antigo dos agentes de atendimento</summary>
                  <div className="text-xs !text-[var(--text-muted)]">Enquanto a transição não termina, estas ferramentas são liberadas quando o agente usa a Skill. Depois, as ferramentas passam a vir só da configuração do agente.</div>
                  <AiToolsPicker value={draft.tools} onChange={(tools) => setDraft({ ...draft, tools })} />
                </details>
              )}
              <div className="space-y-2">
                <h3 className="text-lg font-semibold" style={{ fontSize: 16 }}>Exemplos de conversa</h3>
                <div className="text-xs !text-[var(--text-muted)]">Opcional: como responder em situações típicas.</div>
                {draft.examples.map((ex, i) => (
                  <div key={i} className="space-y-1 skill-example">
                    <Field label="Cliente">
                      <Input onChange={(e) => setDraft({ ...draft, examples: draft.examples.map((x, j) => (j === i ? { ...x, cliente: e.target.value } : x)) })} value={ex.cliente} />
                    </Field>
                    <Field label="Resposta">
                      <Input onChange={(e) => setDraft({ ...draft, examples: draft.examples.map((x, j) => (j === i ? { ...x, agente: e.target.value } : x)) })} value={ex.agente} />
                    </Field>
                    <DangerButton variant="dangerOutline" onClick={() => setDraft({ ...draft, examples: draft.examples.filter((_, j) => j !== i) })}>Remover</DangerButton>
                  </div>
                ))}
                {draft.examples.length < 10 && <Button variant="secondary" icon={ICONS.Plus} onClick={() => setDraft({ ...draft, examples: [...draft.examples, { cliente: '', agente: '' }] })}>Exemplo</Button>}
              </div>
            </section>
          </div>
        )}
      </div>

      <Dialog.Root open={!!history} onOpenChange={(o: boolean) => { if (!o) (() => setHistory(null))(); }}>
<Dialog size="lg" className="p-6">


        {history && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Versões de {history.skill.name}</h3>
            <div className="!text-[var(--text-muted)]">A versão atual é a v{history.skill.version}. Restaurar copia o conteúdo antigo para uma versão nova; nada é apagado.</div>
            <div className="skill-versions">
              <ul>
                {history.versions.map((v) => (
                  <li key={v.id}>
                    <button type="button" className={history.open?.id === v.id ? 'is-open' : ''} onClick={() => setHistory({ ...history, open: v })}>
                      <strong>v{v.version}</strong> <span>{new Date(v.createdAt).toLocaleString('pt-BR')}</span>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="skill-versions__view">
                {history.open ? (
                  <>
                    <div className="font-semibold">{history.open.name} · v{history.open.version}</div>
                    <div className="text-sm !text-[var(--text-muted)]">{history.open.description}</div>
                    <pre>{history.open.instructions}</pre>
                    {history.open.risks && <div className="text-sm">Riscos: {history.open.risks}</div>}
                    {canEdit && history.open.version !== history.skill.version && (
                      <Button variant="secondary" icon={ICONS.Undo} loading={busy} onClick={() => restore(history.open!)}>Restaurar esta versão</Button>
                    )}
                  </>
                ) : <div className="!text-[var(--text-faint)]">Escolha uma versão para ver o conteúdo.</div>}
              </div>
            </div>
          </div>
        )}
      
</Dialog>
</Dialog.Root>

      <Dialog.Root open={generating} onOpenChange={(o: boolean) => { if (!o) (() => setGenerating(false))(); }}>
<Dialog size="base" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">Gerar skill com IA</h3>}</Dialog.Title>

        <div className="space-y-4">
          <InputArea label="Sobre o que é este conhecimento?" onChange={(e) => setGoal(e.target.value)} rows={4} value={goal} placeholder="Ex.: critérios para qualificar quem pede orçamento de energia solar e quando passar para o time técnico" />
          <div className="text-xs !text-[var(--text-muted)]">A IA escreve um rascunho. Revise e reescreva como regras antes de salvar.</div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setGenerating(false)}>Cancelar</Button>
            <Button variant="primary" loading={busy} disabled={goal.trim().length < 10} onClick={generate}>Gerar</Button>
          </div>
        </div>
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={!!deleting} title="Excluir skill" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting && await call(`/api/ai/skills/${deleting.id}`, { method: 'DELETE' })) load(); setDeleting(null); }}>
        A Lumi e os agentes deixam de conhecer <strong>{deleting?.name}</strong>, e o histórico de versões é apagado. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}
